import { Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PATH_TRANSLATIONS, SupportedLang } from '../config/localized-paths';

/**
 * Prepends the active language segment to an app-internal path and translates
 * the path segment to the localized equivalent from PATH_TRANSLATIONS.
 *
 * Usage in templates:
 *   [routerLink]="'/overview' | langLink"
 *   → ['/', 'en', 'overview']  or ['/', 'es', 'descripcion-general']
 *
 * Query parameters must be passed separately via [queryParams]:
 *   [routerLink]="'/aboutus' | langLink" [queryParams]="{contact: 'true'}"
 *
 * The bare root path '/' maps to the home page:
 *   [routerLink]="'/' | langLink"
 *   → ['/', 'en', 'home']
 */
@Pipe({
  name: 'langLink',
  // Mark as impure so the pipe re-evaluates when the active language changes
  pure: false
})
export class LangLinkPipe implements PipeTransform {

  constructor(private readonly translate: TranslateService) {}

  transform(path: string): string[] {
    const lang = (this.translate.currentLang || 'en') as SupportedLang;
    // Strip leading slash and any query string (query params go via [queryParams])
    const canonicalPath = (path ?? '').replace(/^\//, '').split('?')[0] || 'home';
    // Translate the canonical path to the localized segment for the active language.
    // Falls back to the canonical path if the key is not in the map.
    const localizedPath = (PATH_TRANSLATIONS[lang] as Record<string, string>)[canonicalPath] ?? canonicalPath;
    return ['/', lang, localizedPath];
  }
}

